///
/// EarthBrowser
///
/// Redux middleware for state validation
/// Validates state after each action and dispatches fixes through Redux
///

import type { Middleware } from "@reduxjs/toolkit"
import { applyValidatedState, type EarthBrowserState } from "../earthBrowserSlice"
import { validateEarthBrowserState } from "./earthBrowserValidation"

// Action type for applying validated state - used to prevent infinite loops
const APPLY_VALIDATED_STATE_TYPE = "earthbrowser/applyValidatedState"

// Known fields in EarthBrowserState - legacy fields not in this set are expected to be stripped
const KNOWN_FIELDS = new Set([
	"cameraState", "cameraLocation", "earthquakeSearchParams",
	"wildfireRequestParams", "satelliteSets",
	"buoySearchParams",
	"gfsTemperatureSearchParams", "gfsSnowSearchParams", "clockState",
	"clockAnimatingSpeed", "clockCurrentTime", "clockStartOffset", "clockEndOffset",
	"timeZoneMode",
	"visibleDatasets", "loadingDatasets", "sideTableContent", "sideTableSize",
	"timeControlExpanded",
	"volcanoViewMode",
	"municipalityBordersVisible",
	"geopackageLayerVisibility", "geopackageDatasetVisible",
	"externalGeoPackages", "nexradSearchParams"
])

/**
 * Check if two values are deeply equal (for known fields only)
 * Returns list of field paths that differ
 */
function findKnownFieldDifferences(
	original: Record<string, unknown>,
	validated: Record<string, unknown>
): string[] {
	const differences: string[] = []

	for (const key of KNOWN_FIELDS) {
		const origValue = original[key]
		const validValue = validated[key]

		if (JSON.stringify(origValue) !== JSON.stringify(validValue)) {
			differences.push(key)
		}
	}

	return differences
}

/**
 * Middleware that validates the earthBrowser state after each action
 * and dispatches a fix action through Redux if invalid values are detected
 */
export const validationMiddleware: Middleware = (store) => (next) => (action) => {
	// Let the action run first
	const result = next(action)

	// Only validate in browser environment
	if (typeof window === "undefined") {
		return result
	}

	// Get action type
	const actionType = typeof action === "object" && action && "type" in action ? action.type : "unknown"

	// Skip validation for certain actions to prevent infinite loops
	if (typeof actionType === "string" && (
		actionType === APPLY_VALIDATED_STATE_TYPE ||
		actionType.startsWith("persist/") ||
		actionType.includes("REHYDRATE") ||
		actionType.includes("authenticatedApi")
	)) {
		return result
	}

	try {
		const state: unknown = store.getState()

		// Validate earthBrowser slice
		if (state && typeof state === "object" && "earthbrowser" in state) {
			const earthbrowserState = (state as { earthbrowser: EarthBrowserState }).earthbrowser
			const validated = validateEarthBrowserState(earthbrowserState)

			// Check only known fields for differences (ignore legacy field stripping)
			const differences = findKnownFieldDifferences(
				earthbrowserState as unknown as Record<string, unknown>,
				validated as unknown as Record<string, unknown>
			)

			if (differences.length > 0) {
				// Log in development mode
				if (process.env.NODE_ENV !== "production") {
					console.warn(
						`[Validation Middleware] Fixing invalid state after action: ${String(actionType)}\n` +
						`  Correcting ${differences.length} field(s): ${differences.join(", ")}`
					)
				}

				// Dispatch the validated state through Redux
				store.dispatch(applyValidatedState(validated))
			}
		}
	} catch (error) {
		console.error("[Validation Middleware] Error during validation:", error)
	}

	return result
}
